import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  addToCart, 
  registerCheckout, 
  selectCartState, 
  selectIsUserLoggedIn, 
  selectUserInfo,
  selectWithcookieData,
  fetchCheckouts,
  fetchProductDetail,
  selectProductDetailState
} from '../../../redux/WithcookieSlice';
import { showToast } from '../../common/Toast';
import { useTranslation } from '../../../redux/TranslationHelper';
import { MembershipSignupLogicReturn, PriceInfo } from './MembershipSignup.types';

/**
 * 멤버십 가입 로직 훅
 */
export const useMembershipSignupLogic = (props: any): MembershipSignupLogicReturn => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = props?.navigate;
  
  // Redux 상태
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const cartState = useSelector(selectCartState);
  const userInfo = useSelector(selectUserInfo);
  const productDetailState = useSelector(selectProductDetailState);
  const withcookieData = useSelector(selectWithcookieData);
  
  // Props에서 필요한 값 추출
  const productId = typeof props?.options?.productId === 'string' 
    ? parseInt(props.options.productId, 10) || 0 
    : props?.options?.productId || 0;
  
  // 로컬 상태
  const [isRegistered, setIsRegistered] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [prices, setPrices] = useState<PriceInfo[] | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string>('');
  const [selectedAmount, setSelectedAmount] = useState<number>(0);
  
  // 사용자의 멤버십 상태 확인
  const checkMembershipStatus = useCallback(async () => {
    if (isUserLoggedIn && productId > 0) {
      try {
        setIsChecking(true);
        const checkouts = await dispatch(fetchCheckouts({ page: 1, take: 100 }) as any).unwrap();
        
        if (checkouts && Array.isArray(checkouts)) {
          const hasMembership = checkouts.some((checkout: any) => 
            checkout.products && 
            checkout.products.some((product: any) => 
              product.id === productId && 
              (checkout.status === 'checked_out' || checkout.status === 'active')
            )
          );
          
          setIsRegistered(hasMembership);
        }
      } catch (error) {
        console.error('Error checking membership status:', error);
      } finally {
        setIsChecking(false);
      }
    } else {
      setIsChecking(false);
    }
  }, [dispatch, isUserLoggedIn, productId]);
  
  // 상품 정보 가져오기
  const fetchProductInfo = useCallback(async () => {
    if (productId > 0) {
      try {
        const result = await dispatch(fetchProductDetail(productId) as any).unwrap();
        if (result?.prices) {
          setPrices(result.prices);
          // 첫 번째 가격을 기본값으로 설정
          const firstPrice = result.prices[0];
          if (firstPrice) {
            setSelectedPriceId(firstPrice.id.toString());
            setSelectedAmount(parseFloat(firstPrice.unit_amount_decimal || '0'));
          }
        }
      } catch (error) {
        console.error('Error fetching product info:', error);
      }
    }
  }, [dispatch, productId]);
  
  // 멤버십 가입 처리
  const handleMembershipSignup = useCallback(async () => {
    if (!isUserLoggedIn) {
      showToast(t('로그인이 필요합니다.'), 'error');
      if (navigate) {
        navigate('/login');
      }
      return;
    }
    
    if (productId <= 0) {
      showToast(t('유효하지 않은 상품입니다.'), 'error');
      return;
    }
    
    if (!selectedPriceId) {
      showToast(t('가격을 선택해주세요.'), 'error');
      return;
    }
    
    try {
      // 1. 장바구니에 추가
      const cartItem = {
        product_id: productId,
        count: 1,
        priceId: selectedPriceId
      };
      
      await dispatch(addToCart(cartItem) as any).unwrap();
      
      // 2. 체크아웃 등록 - 간단한 형태로 변경
      // const checkoutData = {
      //   items: [{
      //     product_id: productId,
      //     price_id: selectedPriceId,
      //     quantity: 1
      //   }]
      // };
      
      // const checkoutResult = await dispatch(registerCheckout(checkoutData) as any).unwrap();
      
      // if (checkoutResult?.checkout_url) {
      //   // 결제 페이지로 이동
      //   window.location.href = checkoutResult.checkout_url;
      // } else if (selectedAmount === 0) {
      //   // 무료 상품인 경우
      //   setIsRegistered(true);
      //   showToast(t('멤버십 가입이 완료되었습니다.'), 'success');
      // }
      
      // 간단한 완료 처리
      setIsRegistered(true);
      showToast(t('멤버십 가입이 완료되었습니다.'), 'success');
    } catch (error) {
      console.error('Error during membership signup:', error);
      showToast(t('멤버십 가입 중 오류가 발생했습니다.'), 'error');
    }
  }, [dispatch, isUserLoggedIn, productId, selectedPriceId, selectedAmount, t, navigate]);
  
  // 가격 선택 처리
  const handlePriceSelect = useCallback((priceId: string, amount: number) => {
    setSelectedPriceId(priceId);
    setSelectedAmount(amount);
  }, []);
  
  // Effect: 컴포넌트 마운트 시
  useEffect(() => {
    checkMembershipStatus();
    fetchProductInfo();
  }, [checkMembershipStatus, fetchProductInfo]);
  
  return {
    data: {
      isRegistered,
      isChecking,
      isUserLoggedIn,
      prices,
      selectedPriceId,
      selectedAmount,
      productId,
      theme: withcookieData?.theme || {}
    },
    actions: {
      handleMembershipSignup,
      handlePriceSelect,
      checkMembershipStatus
    }
  };
};